/**
 * PRD.330 Phase 4 — Category-bar visualization (no chart dependency).
 *
 * A semantic <table>: the category, amount and count TEXT are authoritative;
 * the bar under each label is decorative (aria-hidden, no focus stop) and
 * sized from RAW numbers via `barFraction`. Data comes only from the
 * visualization's own grouped query result — never from report rows.
 * Loading / empty / error are local to this panel, so a chart failure never
 * blocks the table.
 */
import type { ReactNode } from 'react';
import { Button } from '@helios/ui';
import type { ReportCategoryBarDef, ReportCategoryBarResult } from '@helios/domain';
import { barFraction } from './report-view-logic';
import { selectedValuesText, shouldClearOnEscape } from './category-filter-logic';

const ROW_HEIGHT = 44;

export function ReportCategoryBars({
  visualization,
  result,
  columnLabels,
  formatPrimary,
  emptyMessage = 'No data for the selected filters and date range.',
  colorFor,
  selectedValues = [],
  onCategoryActivate,
  onClearSelection,
  onRetry,
}: {
  visualization: ReportCategoryBarDef<string>;
  result: ReportCategoryBarResult | undefined;
  /** Header text, e.g. { category: 'Payment Method', primary: 'Amount', secondary: 'Payments' }. */
  columnLabels: { category: string; primary: string; secondary?: string };
  formatPrimary: (value: number) => ReactNode;
  emptyMessage?: string;
  /**
   * Optional per-category colours (bar + label text). Categories without one
   * use the brand bar and default text; the text label always identifies the
   * category, so colour is never the only cue.
   */
  colorFor?: (value: string) => { bar: string; text?: string } | undefined;
  /**
   * PRD.330 Phase 5 follow-up — optional interaction. When `onCategoryActivate`
   * is given, each category is a real <button aria-pressed> and the panel
   * shows persistent filter feedback. The component never owns filter state:
   * `selectedValues` is read from the canonical applied query by the caller.
   */
  selectedValues?: ReadonlyArray<string>;
  onCategoryActivate?: (value: string) => void;
  onClearSelection?: () => void;
  onRetry?: () => void;
}) {
  const interactive = onCategoryActivate !== undefined;
  const selected = new Set(selectedValues);
  const labelOf = (value: string) =>
    result?.categories.find((c) => c.value === value)?.label ?? value;
  const categories = result?.categories ?? [];
  const status = result?.status ?? 'loading';
  const hasSecondary = visualization.secondaryMeasure !== undefined;
  const max = categories.reduce((m, c) => Math.max(m, c.primary), 0);
  const empty =
    status === 'ready' && categories.every((c) => c.primary === 0 && (c.secondary ?? 0) === 0);
  const minHeight = Math.max(3, visualization.knownCategories?.length ?? 3) * ROW_HEIGHT + 48;

  // Decorative bar (aria-hidden, not focusable), sized from RAW values.
  const categoryBar = (value: string, primary: number) => (
    <div
      aria-hidden="true"
      style={{
        marginTop: 6,
        height: 8,
        borderRadius: 'var(--radius-full, 999px)',
        background: 'var(--bg-surface-2, #f1f5f9)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${barFraction(primary, max) * 100}%`,
          height: '100%',
          background: colorFor?.(value)?.bar ?? 'var(--color-primary)',
          borderRadius: 'inherit',
        }}
      />
    </div>
  );

  const message = (text: ReactNode, action?: ReactNode) => (
    <div
      style={{
        minHeight: minHeight - 48,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-3)',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        textAlign: 'center',
      }}
    >
      <div>{text}</div>
      {action}
    </div>
  );

  let body: ReactNode;
  if (status === 'error') {
    body = message(
      "Couldn't load this visualization.",
      onRetry && (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      ),
    );
  } else if (status === 'loading' && categories.length === 0) {
    body = message('Loading…');
  } else if (empty) {
    body = message(emptyMessage);
  } else {
    body = (
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          opacity: status === 'loading' ? 0.55 : 1,
          transition: 'opacity 120ms ease',
        }}
      >
        <caption
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {visualization.title}
        </caption>
        <thead>
          <tr>
            {[columnLabels.category, columnLabels.primary, hasSecondary && columnLabels.secondary]
              .filter((h): h is string => typeof h === 'string')
              .map((h, i) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    padding: 'var(--sp-2) var(--sp-3)',
                    textAlign: i === 0 ? 'left' : 'right',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--border)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr
              key={c.value}
              style={{
                height: ROW_HEIGHT,
                // De-emphasize (but keep readable) unselected rows while filtered.
                opacity: selected.size > 0 && !selected.has(c.value) ? 0.6 : 1,
              }}
            >
              <th
                scope="row"
                style={{
                  padding: 'var(--sp-2) var(--sp-3)',
                  textAlign: 'left',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--font-size-sm)',
                  color: colorFor?.(c.value)?.text ?? 'var(--text)',
                  width: '55%',
                }}
              >
                {interactive ? (
                  <button
                    type="button"
                    aria-pressed={selected.has(c.value)}
                    onClick={() => onCategoryActivate(c.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '2px 0',
                      margin: 0,
                      border: 'none',
                      background: 'none',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: selected.has(c.value)
                        ? 'var(--font-weight-semi)'
                        : 'var(--font-weight-medium)',
                    }}
                  >
                    {selected.has(c.value) && <span aria-hidden="true">✓ </span>}
                    {c.label}
                    {categoryBar(c.value, c.primary)}
                  </button>
                ) : (
                  <>
                    {c.label}
                    {categoryBar(c.value, c.primary)}
                  </>
                )}
              </th>
              <td
                style={{
                  padding: 'var(--sp-2) var(--sp-3)',
                  textAlign: 'right',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPrimary(c.primary)}
              </td>
              {hasSecondary && (
                <td
                  style={{
                    padding: 'var(--sp-2) var(--sp-3)',
                    textAlign: 'right',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {c.secondary ?? 0}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Persistent feedback, derived from the canonical selection (not its origin).
  const feedback = interactive && (
    <p
      style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--text-muted)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '2px 6px',
      }}
    >
      {selected.size === 0 ? (
        <span>
          Click a {columnLabels.category.toLowerCase()} to filter the report. Click it again to
          clear the filter.
        </span>
      ) : (
        <>
          <span style={{ color: 'var(--text)' }}>
            Report filtered by {columnLabels.category}:{' '}
            <strong>{selectedValuesText([...selected], labelOf)}</strong>
          </span>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={onClearSelection}
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              font: 'inherit',
              color: 'var(--color-primary)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Clear filter
          </button>
          <span aria-hidden="true">·</span>
          <span>Esc to clear</span>
        </>
      )}
    </p>
  );

  return (
    <section
      aria-label={visualization.title}
      // Escape clears this chart's filter — scoped to this panel (no window /
      // document listener); a keystroke another handler already handled
      // (e.g. an open popover) is ignored.
      onKeyDown={
        interactive
          ? (e) => {
              if (shouldClearOnEscape(e, selected.size)) {
                e.preventDefault();
                onClearSelection?.();
              }
            }
          : undefined
      }
      style={{
        position: 'relative',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-3) var(--sp-4)',
        minHeight,
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <h2
        style={{
          margin: '0 0 var(--sp-2)',
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {visualization.title}
      </h2>
      {feedback}
      {body}
    </section>
  );
}
